import { useMemo, useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { Check } from 'lucide-react';
import { resolveModelDisplayName } from '@reflex/shared';
import type { ModelProvider, ModelProviderSecretType } from '@reflex/shared';
import { cn } from '../../lib/utils';
import {
  getAgentHarnessIconConfig,
  getModelRowSurfaceClass,
  SELECTABLE_MODELS,
} from '../../lib/model-icons';
import { Command, CommandGroup, CommandItem, CommandList } from '../ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  popoverBoundedMaxWidthClass,
} from '../ui/popover';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';
import {
  ModelProviderSecretPicker,
  type ProviderSecretManageAffordance,
  type ProviderSecretRefreshAffordance,
  type ProviderSubscriptionAffordance,
} from '../model-providers/ModelProviderSecretPicker';
import { ModelPickerList, type ModelPickerOption } from './ModelPicker';
import { ModelPickerTriggerContent } from './ModelPickerTriggerContent';

export interface AgentSettingsSelectOption {
  id: string;
  name: string;
  disabled?: boolean;
  /** Hint appended in parentheses on a disabled option (e.g. why an endpoint is unavailable). */
  unavailableReason?: string;
  /** Provider id for grouping agent model options. */
  providerId?: string;
  /** When set, the model list defaults to recommended rows. */
  recommended?: boolean;
}

export interface AgentSettingsReasoningOption {
  id: string;
  name: string;
  default?: boolean;
}

export interface AgentSettingsStatusMessage {
  id: string;
  tone?: 'muted' | 'error';
  text: string;
}

/**
 * Everything needed to render the provider-key pill. When supplied, the
 * chip renders a {@link ModelProviderSecretPicker} as the right pill. The
 * picker keeps its own popover (separate from the agent/model editor), so
 * choosing a key never disturbs agent/model/reasoning controls.
 */
export interface AgentSettingsProviderKeyConfig {
  value: string | null;
  onChange: (id: string | null) => void;
  provider: ModelProvider;
  allowedTypes: readonly ModelProviderSecretType[];
  disabledTypes?: readonly ModelProviderSecretType[];
  owner?: { ownerId: string };
  storageKey?: string;
  onEmptyAddHref?: string;
  allowCreate?: boolean;
  addSubscription?: ProviderSubscriptionAffordance;
  /**
   * Trailing "re-apply this credential" control on the selected row. The
   * composer bar's key pill can be icon-only, so the popover is the only place
   * a running agent can be told to re-push the subscription it already has.
   * Forwarded to {@link ModelProviderSecretPicker}.
   */
  refreshSelected?: ProviderSecretRefreshAffordance;
  /**
   * Trailing cog on the user's own subscription rows, opening the caller's
   * update dialog for that credential. Forwarded to
   * {@link ModelProviderSecretPicker}.
   */
  manageSubscription?: ProviderSecretManageAffordance;
  /**
   * Extra node rendered alongside the picker — e.g. a subscription
   * registration dialog mount whose open state the caller owns.
   */
  dialog?: ReactNode;
  disabled?: boolean;
  /**
   * Collapse the key pill to just the type-distinct icon (subscription
   * vs API key), with the full key details in a popover. Useful for tight,
   * read-only summary bars (e.g. a running agent's composer).
   */
  compact?: boolean;
  /**
   * Visually emphasise the trigger when the surface needs a key chosen
   * before submission. Forwarded to {@link ModelProviderSecretPicker}.
   */
  highlight?: boolean;
  /**
   * Let the picker emit a default selection on its own. Defaults to true;
   * pass `false` when `onChange` writes to the server. Forwarded to
   * {@link ModelProviderSecretPicker}.
   */
  autoSelectDefault?: boolean;
}

export interface AgentSettingsChipProps {
  /** Currently-selected agent type id. */
  agentType: string;
  agentTypeOptions: AgentSettingsSelectOption[];
  onAgentTypeChange: (next: string) => void;

  /** Currently-selected model id (`''` when nothing/inheriting). */
  model: string;
  modelOptions: AgentSettingsSelectOption[];
  onModelChange: (next: string) => void;
  /** Render a free-text combobox (flows) rather than a fixed list. */
  modelFreeText?: boolean;
  /**
   * Model list's read-only state. Overrides `disabled` when set, so a chip
   * locked to its harness can still open onto the model list.
   */
  modelDisabled?: boolean;
  /**
   * Tooltip on the locked trigger. A disabled trigger cannot open its
   * popover, so this is the only way to tell the user why it is locked.
   */
  modelDisabledReason?: string;
  modelPlaceholder?: string;
  /** Footer under the model list (e.g. Manage models link). */
  modelFooter?: ReactNode;

  /**
   * Prefer this provider's group first when grouping models (e.g. the effective
   * provider derived from the current selection).
   */
  preferredProviderId?: string | null;

  /**
   * Manifest-ordered provider ids used as a tiebreak when collapsing
   * direct vs gateway routes of the same logical model.
   */
  providerOrder?: readonly string[];

  /** Reasoning-effort control — only rendered when options are supplied. */
  reasoningOptions?: AgentSettingsReasoningOption[];
  reasoningEffort?: string | null;
  onReasoningEffortChange?: (next: string) => void;
  /** Effort picker's read-only state. Overrides `disabled` when set. */
  reasoningDisabled?: boolean;

  /** Endpoint-route control — only rendered when options + handler are supplied (matrix agents). */
  endpointOptions?: AgentSettingsSelectOption[];
  endpointValue?: string | null;
  onEndpointChange?: (next: string) => void;
  endpointPlaceholder?: string;

  /** Status notes rendered under the controls (catalog loading/availability). */
  statusMessages?: AgentSettingsStatusMessage[];

  /** Provider-key pill. Omit to hide the key affordance entirely. */
  providerKey?: AgentSettingsProviderKeyConfig;

  disabled?: boolean;
  /**
   * Let the closed left-pill model name truncate with the catalog short name
   * on narrow viewports (same as {@link ModelPicker} compact). Key collapses
   * to icon-only; effort is always bars-only.
   */
  compact?: boolean;
  className?: string;
  'data-testid'?: string;
}

const TRIGGER_CLASS =
  'group h-auto max-w-60 min-w-0 justify-start gap-1.5 px-2 py-1.5 text-xs font-medium';

function SecondaryFieldList({
  options,
  value,
  onSelect,
  heading,
  listTestId,
}: {
  options: AgentSettingsSelectOption[];
  value: string;
  onSelect: (next: string) => void;
  heading: string;
  listTestId?: string;
}) {
  return (
    <Command shouldFilter={false}>
      <CommandList className="max-h-none overflow-visible" data-testid={listTestId}>
        <CommandGroup heading={heading}>
          {options.map((o) => (
            <CommandItem
              key={o.id}
              value={`${o.name} ${o.id}`}
              disabled={o.disabled}
              onSelect={() => {
                if (!o.disabled) onSelect(o.id);
              }}
              className={cn('gap-2', getModelRowSurfaceClass(undefined, o.id === value))}
            >
              <span className="flex-1 truncate">{o.name}</span>
              {o.disabled && o.unavailableReason ? (
                <span className="shrink-0 text-2xs text-muted-foreground">
                  ({o.unavailableReason})
                </span>
              ) : o.id === value ? (
                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
              ) : null}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function AgentTypeHeader({
  agentType,
  agentTypeOptions,
  onAgentTypeChange,
}: {
  agentType: string;
  agentTypeOptions: AgentSettingsSelectOption[];
  onAgentTypeChange: (next: string) => void;
}) {
  // Keep tabs (plus a "More" overflow) so the popover has a single search
  // input — the model list's.
  const TAB_LIMIT = 4;
  const tabOptions = agentTypeOptions.slice(0, TAB_LIMIT);
  const overflowOptions = agentTypeOptions.slice(TAB_LIMIT);
  const selectedInOverflow = overflowOptions.some((o) => o.id === agentType);
  const [moreOpen, setMoreOpen] = useState(false);

  const tabsValue = selectedInOverflow ? '' : agentType;

  return (
    <div className="border-b border-border p-1.5" data-tutorial-id="agent-type-menu">
      <Tabs value={tabsValue} onValueChange={onAgentTypeChange}>
        <TabsList className="h-8 w-full">
          {tabOptions.map((opt) => {
            const harness = getAgentHarnessIconConfig(opt.id);
            const isActive = opt.id === tabsValue;
            return (
              <TabsTrigger
                key={opt.id}
                value={opt.id}
                disabled={opt.disabled}
                className="gap-1 border-transparent px-1.5 text-2xs hover:bg-background/70 data-[state=active]:border-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none dark:data-[state=active]:border-transparent dark:data-[state=active]:bg-transparent"
                title={opt.name}
              >
                {isActive ? (
                  <motion.span
                    layoutId="agent-type-tab-indicator"
                    aria-hidden
                    className="absolute -inset-px rounded-md border border-transparent bg-background shadow-sm dark:border-input"
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  />
                ) : null}
                <span className="relative z-10 flex min-w-0 items-center gap-1">
                  {harness ? (
                    <harness.icon
                      className={cn('h-3.5 w-3.5 shrink-0', !harness.isBrandSvg && harness.color)}
                    />
                  ) : null}
                  <span className="min-w-0 truncate">{opt.name}</span>
                </span>
              </TabsTrigger>
            );
          })}
          {overflowOptions.length > 0 ? (
            <Popover open={moreOpen} onOpenChange={setMoreOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedInOverflow}
                  data-state={selectedInOverflow ? 'active' : 'inactive'}
                  data-testid="agent-type-more-trigger"
                  className={cn(
                    'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 text-2xs font-medium',
                    'ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    'hover:bg-background/70 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
                    'text-muted-foreground',
                  )}
                >
                  <span className="truncate">
                    {selectedInOverflow
                      ? (overflowOptions.find((o) => o.id === agentType)?.name ?? 'More')
                      : 'More'}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                className={cn('w-48 overflow-hidden p-0', popoverBoundedMaxWidthClass('14rem'))}
                align="end"
                data-testid="agent-type-more-menu"
              >
                <Command shouldFilter={false}>
                  <CommandList className="max-h-48">
                    <CommandGroup>
                      {overflowOptions.map((o) => {
                        const harness = getAgentHarnessIconConfig(o.id);
                        return (
                          <CommandItem
                            key={o.id}
                            value={`${o.name} ${o.id}`}
                            disabled={o.disabled}
                            data-testid={`agent-type-more-${o.id}`}
                            onSelect={() => {
                              if (!o.disabled) {
                                onAgentTypeChange(o.id);
                                setMoreOpen(false);
                              }
                            }}
                            className={cn(
                              'gap-2',
                              getModelRowSurfaceClass(harness?.bgColor, o.id === agentType),
                            )}
                          >
                            {harness ? (
                              <harness.icon
                                className={cn(
                                  'h-3.5 w-3.5 shrink-0',
                                  !harness.isBrandSvg && harness.color,
                                )}
                              />
                            ) : null}
                            <span className="flex-1 truncate">{o.name}</span>
                            {o.id === agentType ? (
                              <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                            ) : null}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          ) : null}
        </TabsList>
      </Tabs>
    </div>
  );
}

function ReasoningEffortBars({ level, count }: { level: number; count: number }) {
  const bars = Math.max(count, 1);
  return (
    <span aria-hidden data-testid="reasoning-effort-bars" className="flex h-3 items-end gap-[2px]">
      {Array.from({ length: bars }, (_, i) => (
        <span
          key={i}
          className={cn(
            'w-[3px] rounded-[1px] bg-current',
            i <= level ? 'opacity-100' : 'opacity-30',
          )}
          style={{ height: `${Math.round(((i + 1) / bars) * 100)}%` }}
        />
      ))}
    </span>
  );
}

function HairlineSeparator() {
  return (
    <div
      className="h-5 w-px shrink-0 bg-border"
      aria-hidden="true"
      data-testid="agent-settings-separator"
    />
  );
}

function EffortPicker({
  reasoningOptions,
  effectiveReasoning,
  onReasoningEffortChange,
  reasoningLevel,
  disabled,
}: {
  reasoningOptions: AgentSettingsReasoningOption[];
  effectiveReasoning: string;
  onReasoningEffortChange?: (next: string) => void;
  reasoningLevel: number;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selectedName =
    reasoningOptions.find((o) => o.id === effectiveReasoning)?.name ?? effectiveReasoning;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label={`Reasoning effort: ${selectedName}`}
          title={selectedName}
          disabled={disabled}
          data-testid="agent-settings-effort-trigger"
          className="h-auto justify-center gap-0 px-2 py-1.5"
        >
          <span
            className="flex shrink-0 items-center text-muted-foreground"
            data-testid="agent-settings-chip-reasoning"
          >
            <ReasoningEffortBars level={reasoningLevel} count={reasoningOptions.length} />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('overflow-hidden p-0', 'w-56', popoverBoundedMaxWidthClass('14rem'))}
        align="start"
        data-testid="agent-settings-effort-editor"
      >
        <Command shouldFilter={false}>
          <CommandList className="max-h-72">
            <CommandGroup>
              {reasoningOptions.map((opt, i) => {
                const selected = opt.id === effectiveReasoning;
                return (
                  <CommandItem
                    key={opt.id}
                    value={`${opt.name} ${opt.id}`}
                    data-testid={`agent-settings-effort-${opt.id}`}
                    onSelect={() => {
                      onReasoningEffortChange?.(opt.id);
                      setOpen(false);
                    }}
                    className={cn('gap-2', getModelRowSurfaceClass(undefined, selected))}
                  >
                    <ReasoningEffortBars level={i} count={reasoningOptions.length} />
                    <span className="flex-1 truncate">{opt.name}</span>
                    {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" /> : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Compact settings bar: ghost model trigger opens agent + model popover;
 * optional effort picker and provider-key picker sit beside it with hairline
 * separators (create-automation generate chrome). Pure and fully controlled.
 */
export function AgentSettingsChip({
  agentType,
  agentTypeOptions,
  onAgentTypeChange,
  model,
  modelOptions,
  onModelChange,
  modelFreeText = false,
  modelDisabled,
  modelDisabledReason,
  modelPlaceholder = 'Select model',
  modelFooter,
  preferredProviderId,
  providerOrder,
  reasoningOptions,
  reasoningEffort,
  onReasoningEffortChange,
  reasoningDisabled,
  endpointOptions,
  endpointValue,
  onEndpointChange,
  statusMessages,
  providerKey,
  disabled = false,
  compact = false,
  className,
  'data-testid': dataTestId,
}: AgentSettingsChipProps) {
  const [open, setOpen] = useState(false);

  // `disabled` locks the harness. The trigger also opens the model list, so it
  // stays enabled while either one can change.
  const modelLocked = modelDisabled ?? disabled;
  const triggerDisabled = disabled && modelLocked;

  const showReasoning = Boolean(reasoningOptions && reasoningOptions.length > 0);
  const showEndpoint = Boolean(endpointOptions && endpointOptions.length > 0 && onEndpointChange);

  const modelName = useMemo(
    () => modelOptions.find((o) => o.id === model)?.name ?? resolveModelDisplayName(model),
    [modelOptions, model],
  );

  const effectiveReasoning =
    reasoningEffort ??
    reasoningOptions?.find((o) => o.default)?.id ??
    reasoningOptions?.[0]?.id ??
    '';
  const reasoningLevel = (reasoningOptions ?? []).findIndex((o) => o.id === effectiveReasoning);

  const modelListOptions = useMemo<ModelPickerOption[]>(() => {
    if (modelOptions.length > 0) {
      return modelOptions.map((m) => ({
        id: m.id,
        name: m.name,
        providerId: m.providerId,
        recommended: m.recommended,
        disabled: m.disabled,
        unavailableReason: m.unavailableReason,
      }));
    }
    if (modelFreeText) return SELECTABLE_MODELS.map((m) => ({ id: m.id, name: m.name }));
    return [];
  }, [modelOptions, modelFreeText]);

  const trailingSection = showEndpoint ? (
    <SecondaryFieldList
      heading="Endpoint"
      options={endpointOptions ?? []}
      value={endpointValue ?? ''}
      onSelect={(next) => onEndpointChange?.(next)}
    />
  ) : null;

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        role="combobox"
        aria-expanded={open}
        aria-label="Edit agent and model"
        disabled={triggerDisabled}
        data-testid="agent-settings-trigger"
        className={cn(TRIGGER_CLASS, compact && 'max-w-40')}
      >
        <ModelPickerTriggerContent
          value={model || null}
          label={modelName || undefined}
          placeholder={modelPlaceholder}
          size="compact"
        />
      </Button>
    </PopoverTrigger>
  );

  return (
    <div
      className={cn('flex min-w-0 flex-col gap-1.5', disabled && 'select-none', className)}
      data-testid={dataTestId ?? 'agent-settings-chip'}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <Popover open={open} onOpenChange={setOpen}>
          {triggerDisabled && modelDisabledReason ? (
            // The disabled button is `pointer-events-none` and unfocusable, so
            // the tooltip hangs on a focusable span that still takes hover and
            // focus.
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="flex min-w-0">
                  {trigger}
                </span>
              </TooltipTrigger>
              <TooltipContent>{modelDisabledReason}</TooltipContent>
            </Tooltip>
          ) : (
            trigger
          )}
          <PopoverContent
            className={cn(
              'flex w-96 flex-col overflow-hidden p-0 z-[60]',
              popoverBoundedMaxWidthClass('24rem'),
            )}
            align="start"
            data-testid="agent-settings-editor"
            collisionPadding={16}
            // Portaled to <body> and sized against the viewport, so inside a
            // dialog the popover can extend past the dialog's edges instead of
            // shrinking the model list to whatever space the dialog leaves.
            // A modal dialog's scroll lock cancels wheel/touch scrolling
            // outside its content; stopping propagation keeps those events
            // from reaching the lock's document listener so the list scrolls.
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
            onOpenAutoFocus={(event) => {
              const content = event.currentTarget;
              if (!(content instanceof HTMLElement)) return;
              const search = content.querySelector<HTMLInputElement>('[data-slot="command-input"]');
              if (search) {
                event.preventDefault();
                search.focus();
              }
            }}
            onFocusOutside={(event) => event.preventDefault()}
          >
            {disabled ? null : (
              <AgentTypeHeader
                agentType={agentType}
                agentTypeOptions={agentTypeOptions}
                onAgentTypeChange={onAgentTypeChange}
              />
            )}
            <ModelPickerList
              value={model || undefined}
              onChange={(next) => {
                if (next) onModelChange(next);
                setOpen(false);
              }}
              options={modelListOptions}
              allowInherit={false}
              allowCustom={modelFreeText}
              searchPlaceholder={modelFreeText ? 'Search or type a model…' : 'Search models…'}
              footer={modelFooter}
              preferredProviderId={preferredProviderId}
              providerOrder={providerOrder}
              trailingSection={trailingSection}
              disabled={modelLocked}
              fillAvailable
              listTutorialId="model-menu"
              resetKey={open}
            />
          </PopoverContent>
        </Popover>

        {showReasoning && reasoningOptions ? (
          <>
            <HairlineSeparator />
            <EffortPicker
              reasoningOptions={reasoningOptions}
              effectiveReasoning={effectiveReasoning}
              onReasoningEffortChange={onReasoningEffortChange}
              reasoningLevel={reasoningLevel}
              disabled={reasoningDisabled ?? disabled}
            />
          </>
        ) : null}

        {providerKey ? (
          <>
            <HairlineSeparator />
            <ModelProviderSecretPicker
              value={providerKey.value}
              onChange={providerKey.onChange}
              provider={providerKey.provider}
              allowedTypes={providerKey.allowedTypes}
              disabledTypes={providerKey.disabledTypes}
              owner={providerKey.owner}
              storageKey={providerKey.storageKey}
              onEmptyAddHref={providerKey.onEmptyAddHref}
              allowCreate={providerKey.allowCreate}
              addSubscription={providerKey.addSubscription}
              refreshSelected={providerKey.refreshSelected}
              manageSubscription={providerKey.manageSubscription}
              // The key pill can stay interactive even when the left pill is
              // read-only (e.g. a running agent whose type/model are fixed but
              // whose subscription can still be switched).
              disabled={providerKey.disabled ?? disabled}
              compact={providerKey.compact}
              ghost
              highlight={providerKey.highlight}
              autoSelectDefault={providerKey.autoSelectDefault}
              className={providerKey.compact ? undefined : TRIGGER_CLASS}
            />
          </>
        ) : null}

        {providerKey?.dialog}
      </div>

      {statusMessages && statusMessages.length > 0 ? (
        <div className="space-y-1" data-testid="agent-settings-status">
          {statusMessages.map((m) => (
            <p
              key={m.id}
              className={cn(
                'text-2xs',
                m.tone === 'error' ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {m.text}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
